import type { TaskStatus } from "@erp/shared-types";

/**
 * Canonical task status transition rules — the single source of truth
 * shared by the backend (TaskService validates transitions against these)
 * and the frontend (the status picker offers only legal next states).
 * Keyed by `TaskStatus` so adding a status to @erp/shared-types forces an
 * entry here.
 */
export const TASK_STATUS_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  pending: ["in_progress", "completed", "failed", "cancelled"],
  in_progress: ["pending", "completed", "failed", "cancelled", "needs_input"],
  failed: ["pending", "cancelled", "needs_input"],
  cancelled: ["pending"],
  completed: ["pending", "cancelled"],
  needs_input: ["pending", "cancelled"],
};

/** Legal next statuses reachable from `current` (excludes `current` itself). */
export function getAllowedTaskStatusTransitions(
  current: TaskStatus,
): TaskStatus[] {
  return TASK_STATUS_TRANSITIONS[current] ?? [];
}

/**
 * Whether a task may move from `from` to `to`. Accepts plain strings so
 * backend callers using a separate TaskStatus enum can pass enum values
 * (whose underlying strings match) without a cast.
 */
export function canTransitionTaskStatus(from: string, to: string): boolean {
  const allowed: readonly string[] =
    TASK_STATUS_TRANSITIONS[from as TaskStatus] ?? [];
  return allowed.includes(to);
}
